import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaArrowLeft, FaEnvelope, FaPhone, FaUser } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const password = localStorage.getItem('adminPassword');

  useEffect(() => {
    if (!password) {
      navigate('/admin');
      return;
    }
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/enquiries?password=${password}`);
      setEnquiries(response.data);
    } catch (error) {
      toast.error('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (enquiryId, status) => {
    try {
      await axios.patch(`${API}/enquiries/${enquiryId}/status`, {
        status,
        password
      });
      toast.success('Status updated');
      fetchEnquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <div className='bg-[#0F5132] text-white py-6 shadow-lg'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center space-x-4'>
              <Link to='/admin/dashboard' className='text-white hover:text-gray-200'>
                <FaArrowLeft size={24} />
              </Link>
              <div>
                <h1 className='text-3xl font-bold'>Enquiries</h1>
                <p className='text-gray-200 mt-1'>Total: {enquiries.length} enquiries</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {loading ? (
          <div className='flex justify-center py-20'>
            <div className='animate-spin rounded-full h-12 w-12 border-4 border-[#0F5132] border-t-transparent'></div>
          </div>
        ) : enquiries.length === 0 ? (
          <div className='bg-white rounded-xl shadow-lg p-12 text-center'>
            <FaEnvelope className='mx-auto text-6xl text-gray-300 mb-4' />
            <h3 className='text-2xl font-bold text-gray-700 mb-2'>No enquiries yet</h3>
            <p className='text-gray-600'>Enquiries from your website will appear here</p>
          </div>
        ) : (
          <div className='space-y-4'>
            {enquiries.map((enquiry) => (
              <div key={enquiry.id} className='bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow'>
                <div className='flex justify-between items-start mb-4'>
                  <div>
                    <h3 className='text-xl font-bold text-[#0F5132] mb-1'>{enquiry.name}</h3>
                    <p className='text-sm text-gray-500'>
                      {new Date(enquiry.created_at).toLocaleString()}
                    </p>
                  </div>
                  <select
                    value={enquiry.status}
                    onChange={(e) => updateStatus(enquiry.id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(enquiry.status)}`}
                  >
                    <option value='new'>New</option>
                    <option value='contacted'>Contacted</option>
                    <option value='closed'>Closed</option>
                  </select>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-4'>
                  <div className='flex items-center space-x-2 text-gray-700'>
                    <FaEnvelope className='text-[#0F5132]' />
                    <a href={`mailto:${enquiry.email}`} className='hover:underline'>{enquiry.email}</a>
                  </div>
                  <div className='flex items-center space-x-2 text-gray-700'>
                    <FaPhone className='text-[#0F5132]' />
                    <a href={`tel:${enquiry.phone}`} className='hover:underline'>{enquiry.phone}</a>
                  </div>
                </div>

                {enquiry.brand_name && (
                  <div className='bg-gray-50 rounded-lg p-4 mb-4'>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm'>
                      <div>
                        <span className='font-semibold text-gray-700'>Brand:</span> {enquiry.brand_name}
                      </div>
                      <div>
                        <span className='font-semibold text-gray-700'>Product Type:</span> {enquiry.product_type || 'N/A'}
                      </div>
                      <div>
                        <span className='font-semibold text-gray-700'>Quantity:</span> {enquiry.quantity || 'N/A'}
                      </div>
                    </div>
                  </div>
                )}

                <div className='bg-blue-50 rounded-lg p-4'>
                  <p className='font-semibold text-gray-700 mb-2'>Message:</p>
                  <p className='text-gray-600'>{enquiry.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnquiries;
